import React, { useEffect, useState, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import { defineMessages } from 'react-intl';
import Isvg from 'react-inlinesvg';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import Button from 'components/Button';
import { ICONS, PFE_ICONS } from 'constants/icons';
import Message from 'components/Message';

import braze, { BRAZE_CUSTOM_EVENTS } from 'helpers/braze';
import { bem } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import './SmartSpotBanner.scss';

const SPEED_FAST = 300;

const { block, element } = bem('smart-spot-banner');
const messages = defineMessages({
  headline: {
    id: 'smart-spot-banner.headline',
    defaultMessage: 'Earn more money by doing less',
  },
  biline: {
    id: 'smart-spot-banner.biline',
    defaultMessage: 'SmartSpot automates your spot allocation to help you earn more by taking out the guesswork.',
  },
  reason1: {
    id: 'smart-spot-banner.reason-1',
    defaultMessage: 'SmartSpot looks at every class by day, time and staff',
  },
  reason2: {
    id: 'smart-spot-banner.reason-2',
    defaultMessage: 'Spots are automatically released to maximize earnings for every class',
  },
  reason3: {
    id: 'smart-spot-banner.reason-3',
    defaultMessage: 'Most importantly – SmartSpot protects the spots you fill directly',
  },
  turnOnSmartSpot: {
    id: 'smart-spot-banner.turn-on-button',
    defaultMessage: 'Turn on SmartSpot',
  },
  finePrint: {
    id: 'smart-spot-banner.fine-print',
    defaultMessage: 'You can manage SmartSpot in your venue settings at any time.',
  },
});

const SmartSpotBannerList = ({ children }) => (
  <ul className={element('reason-list', 'text--sm')}>
    {React.Children.toArray(children).map((child, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <li key={i} className={element('reason-list-item')}>
        <Isvg className={element('checkmark')} src={ICONS.checkmark} />
        {child}
      </li>
    ))}
  </ul>
);

SmartSpotBannerList.propTypes = {
  children: PropTypes.node,
};

const SmartSpotBanner = ({ dismissBanner, showSmartSpotBanner, venueId, className }) => {
  const [showBanner, setShowBanner] = useState(showSmartSpotBanner);
  const [height, setHeight] = useState(null);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    setShowBanner(showSmartSpotBanner);
  }, [showSmartSpotBanner]);

  const handleTurnOnSmartSpotClick = () => {
    braze.trackEvent(BRAZE_CUSTOM_EVENTS.SMARTSPOT_ON);
    navigate(makePath.smartTools(venueId));
  };

  const button = (
    <Message
      className={element('button')}
      as={Button}
      styled="tertiary"
      message={messages.turnOnSmartSpot}
      onClick={handleTurnOnSmartSpotClick}
    />
  );
  const finePrint = (
    <Message className={element('fine-print', 'mt-r mb-0 text--xs')} as="p" message={messages.finePrint} />
  );

  return (
    <CSSTransition
      className={block(className)}
      in={showBanner}
      timeout={SPEED_FAST * 2} // This animation runs in 2 steps -- fade out, and then slide up
      onEnter={() => setHeight(null)}
      onExit={() => setHeight(ref.current.offsetHeight)}
      onExiting={() => setHeight(0)}
      onExited={() => setHeight(null)}
      unmountOnExit>
      <section ref={ref} style={{ height }}>
        <button type="button" className={element('close-button')} onClick={dismissBanner}>
          <Isvg src={ICONS.crossSmall} />
        </button>
        <div className={element('graph-image')}>
          <Isvg src={PFE_ICONS.smartSpotGraph} />
        </div>
        <div className={element('hero')}>
          <Message className={element('headline')} as="h4" message={messages.headline} />
          <Message as="p" message={messages.biline} />
          {button}
        </div>

        <div className={element('reasons-wrap')}>
          <SmartSpotBannerList>
            <Message message={messages.reason1} />
            <Message message={messages.reason2} />
            <Message message={messages.reason3} />
          </SmartSpotBannerList>
          {finePrint}
        </div>

        {button}
        {finePrint}
      </section>
    </CSSTransition>
  );
};

SmartSpotBanner.propTypes = {
  showSmartSpotBanner: PropTypes.bool,
  className: PropTypes.string,
  dismissBanner: PropTypes.func,
  venueId: PropTypes.number.isRequired,
};

export default SmartSpotBanner;
